import type { HeroState, WorldState } from '../../shared/game/world-types.js';
import { abilityEventVoices, abilityVoiceEvents, combatEventVoices } from '../../content/voices/voice-events.js';

/** Seconds after the player's own ability or potion order in which the matching change in the world is theirs, not the hero's. */
const MANUAL_SECONDS = 4;

interface Seen { order: HeroState['combat']['order']; targetId: string | null; routing: boolean; casting: string | null; charges: Map<string, number>; item: boolean; dying: boolean }
const seenOf = (hero: HeroState): Seen => ({
  order: hero.combat.order, targetId: hero.combat.targetId, routing: hero.route.length > 0, casting: hero.class.casting?.abilityId ?? null,
  charges: new Map(hero.class.abilities.map(ability => [ability.id, ability.charges])), item: hero.combat.windup?.kind === 'item',
  dying: !!hero.health.dying,
});

/**
 * Heroes speak for what they decide to do on their own, as they do for orders. The server marks the player's orders and the hero's
 * own choices apart (`order: 'automatic'` is the behavior system's), so this watches snapshots for a hero taking a new target or
 * moving to a position of their own accord, casting or spending a power, and starting a potion. The first snapshot, and the first
 * after a restart, only sets the baseline, so joining a fight in progress is silent.
 */
export class AutoVoices {
  private revision = -1;
  private eventId = 0;
  private previous = new Map<string, Seen>();
  private manualUntil = new Map<string, number>();

  constructor(private say: (heroId: string, event: string) => void, private now: () => number = () => performance.now() / 1000, private visible: (heroId: string) => boolean = () => true) {}

  /** The player has just ordered an ability or potion for these heroes, so the change it causes is not their own doing. */
  manual(heroIds: string[]) { for (const id of heroIds) this.manualUntil.set(id, this.now() + MANUAL_SECONDS); }
  reset() { this.previous.clear(); this.manualUntil.clear(); this.revision = -1; this.eventId = 0; }

  receive(snapshot: Pick<WorldState, 'revision' | 'heroes' | 'visualEvents'>, heroIds: string[]) {
    const fresh = snapshot.revision === this.revision ? snapshot.visualEvents.filter(event => event.id > this.eventId) : [];
    if (snapshot.revision !== this.revision) { this.revision = snapshot.revision; this.previous.clear(); }
    this.eventId = Math.max(this.eventId, ...snapshot.visualEvents.map(event => event.id));
    this.battleCries(snapshot, fresh, heroIds);
    const now = this.now();
    for (const hero of snapshot.heroes) {
      if (!heroIds.includes(hero.id)) continue;
      const seen = seenOf(hero), before = this.previous.get(hero.id);
      this.previous.set(hero.id, seen);
      if (!before || hero.health.dead) continue;
      if (seen.dying) {
        if (!before.dying) this.say(hero.id, 'dying');
        continue;
      }
      const own = now >= (this.manualUntil.get(hero.id) ?? 0);
      const event = own && this.powerOrPotion(before, seen) || this.choice(before, seen);
      if (event) this.say(hero.id, event);
    }
    // The Berserker's war cry and That all you got?!, a Sneak Attack, a Defend, an Interpose, Assess and Anticipate Danger, before the
    // pain of the same blow.
    for (const event of fresh) {
      const reaction = combatEventVoices[event.kind];
      const line = event.kind === 'ability' ? abilityEventVoices[event.abilityId ?? ''] : event.kind === 'impact' && event.sneakAttack ? 'sneak-attack' : reaction?.event;
      const speaker = reaction?.speaker === 'target' ? event.targetId : event.actorId;
      const hero = line && snapshot.heroes.find(hero => hero.id === speaker);
      if (line && hero && !hero.health.dead && !hero.health.dying && this.visible(hero.id)) this.say(hero.id, line);
    }
    const hurt = new Map<string, boolean>();
    for (const event of fresh) {
      if (event.kind !== 'impact' || !(event.hpDamage && event.hpDamage > 0)) continue;
      const hero = snapshot.heroes.find(hero => hero.id === event.targetId);
      if (!hero || hero.health.dead || hero.health.dying || !this.visible(hero.id)) continue;
      hurt.set(hero.id, (hurt.get(hero.id) ?? false) || event.critical);
    }
    // Critical reactions get the first chance to speak when several hits arrive together.
    for (const [id, critical] of [...hurt].sort((a, b) => Number(b[1]) - Number(a[1]))) this.say(id, critical ? 'critical-pain' : 'pain');
  }

  /** A hero's blow that is a critical hit or fells a foe is cried out. Only blows landed since the last snapshot count. */
  private battleCries(snapshot: Pick<WorldState, 'heroes'>, fresh: WorldState['visualEvents'], heroIds: string[]) {
    const fighters = new Set<string>();
    for (const event of fresh) {
      if (event.kind !== 'impact' || !(event.critical || event.lethal) || !heroIds.includes(event.actorId)) continue;
      const striker = snapshot.heroes.find(hero => hero.id === event.actorId);
      if (striker && !striker.health.dead && !snapshot.heroes.some(hero => hero.id === event.targetId)) fighters.add(striker.id);
    }
    for (const id of fighters) this.say(id, 'battlecry');
  }

  private powerOrPotion(before: Seen, now: Seen): string | null {
    if (now.casting && now.casting !== before.casting) return abilityVoiceEvents[now.casting] ?? null;
    for (const [id, charges] of now.charges) if (charges < (before.charges.get(id) ?? charges)) return abilityVoiceEvents[id] ?? null;
    return now.item && !before.item ? 'potion' : null;
  }

  /** A new target or a new walk chosen under the hero's behavior settings. */
  private choice(before: Seen, now: Seen): string | null {
    if (now.order !== 'automatic') return null;
    if (now.targetId && now.targetId !== before.targetId) return before.order === 'automatic' && before.targetId ? 'change-target' : 'attack';
    return !now.targetId && now.routing && !(before.order === 'automatic' && !before.targetId && before.routing) ? 'combat-move' : null;
  }
}
